import { Routes } from '@angular/router';

import {
  CUSTOMER_ADVISORS_ROUTE,
  DEFAULT_ROUTE,
  INCIDENT_MESSAGES_ROUTE,
  MATERIAL_REPORT_ROUTE,
  MATERIAL_VIEW_ROUTE,
  PARKING_PLAN_ROUTE,
  REVERSAL_MATERIAL_ROUTE,
  TRAFFIC_OVERVIEW_ROUTE,
  TRAIN_CARD_ROUTE,
  VACATION_APPLICATION_ROUTE,
  CLEANING_INFO_ROUTE,
  STATIONS_MONITOR_ROUTE,
  CONTACT_INFO_ROUTE,
  DUTY_KPI_INFO_ROUTE,
  RTV_ROUTE,
  HOW_TO,
  AI_CHATBOT_ROUTE,
  SETTINGS_ROUTE,
  DEPOT_MANAGEMENT_ROUTE
} from '@gan/staff/common';

import { AccessScopesUnion } from '@gan/core/model-access-scopes';
import { AuthGuard, HasScopeGuard } from '@gan/common/feature-auth';
import { SetTitleGuard } from '@gan/shared/ui-title';

import { SanityIntraStaffService, SanityNorgeService } from '@gan/core/util-sanity';

import {
  BusReplacementsSignalRService,
  BusReplacementService,
  ReplacementService,
} from '@gan/common/data-bus-replacements';

import {
  BusReplacementService as StaffBusReplacementService,
  BusReplacementsSignalRService as StaffBusReplacementsSignalRService,
} from '@gan/staff/data-bus-replacements';

import { TrainCardService, TrainCardSignalRService } from '@gan/common/data-train-card';
import {
  TrainCardService as StaffTrainCardService,
  TrainCardSignalRService as StaffTrainCardSignalRService,
} from '@gan/staff/data-train-card';

import { PunctualityKpiService, providePunctuality } from '@gan/common/data-punctuality';
import { provideStations } from '@gan/common/data-stations';
import { provideVehicleChecklists } from '@gan/common/data-vehicle-checklists';
import { provideCustomerAdvisors } from '@gan/common/data-customer-advisors';
import { provideParkingPlan } from '@gan/common/data-parking-plan';
import { provideStaffInternMessages } from '@gan/common/data-staff-intern-messages';
import { provideTrainIncidentLogsCount } from '@gan/common/data-train-incident-logs-count';
import { provideUserNotifications } from '@gan/common/data-user-notifications';
import { provideVehicleErrorsState } from '@gan/common/data-vehicle-errors-state';
import { provideVehiclePosition } from '@gan/common/data-vehicle-position';
import { provideDeviations } from '@gan/common/data-deviations';
import { provideRto } from '@gan/common/data-rto';
import { provideCleaningInfo } from '@gan/common/data-cleaning-info';
import { provideBoarding } from '@gan/common/data-boarding';
import { provideMaterialOverview } from '@gan/common/data-material-overview';
import { provideStaffManagement } from '@gan/common/data-staff-management';
import { provideHiddenDutyTrains } from '@gan/common/data-hidden-duty-trains';
import { provideIduCode } from '@gan/common/data-idu-code';

export const APP_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./layout/default/default-layout.component').then(({ DefaultLayoutComponent }) => DefaultLayoutComponent),
    canActivate: [AuthGuard],
    providers: [
      PunctualityKpiService,
      provideStaffInternMessages(),
      provideTrainIncidentLogsCount(),
      provideUserNotifications()
    ],
    children: [
      {
        path: '',
        redirectTo: DEFAULT_ROUTE,
        pathMatch: 'full'
      },
      {
        path: TRAFFIC_OVERVIEW_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'TrafficOverview'] as AccessScopesUnion[],
          title: 'MAIN_MENU.TRAFFIC_OVERVIEW'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadComponent: () =>
          import('./traffic-overview/traffic-overview.component').then(
            ({ TrafficOverviewComponent }) => TrafficOverviewComponent
          )
      },
      {
        path: TRAIN_CARD_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'TrainServiceRead'] as AccessScopesUnion[],
          title: 'APP.TITLE'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadChildren: () => import('@gan/common/feature-train-card').then(({ TRAIN_CARD_ROUTES }) => TRAIN_CARD_ROUTES),
        providers: [
          provideVehicleChecklists(),
          provideStations(),
          provideVehiclePosition(),
          provideBoarding(),
          provideCleaningInfo(),
          provideMaterialOverview(),
          provideStaffManagement(),
          provideHiddenDutyTrains(),
          ReplacementService,
          { provide: BusReplacementService, useClass: StaffBusReplacementService },
          { provide: BusReplacementsSignalRService, useClass: StaffBusReplacementsSignalRService },
          { provide: TrainCardService, useClass: StaffTrainCardService },
          { provide: TrainCardSignalRService, useClass: StaffTrainCardSignalRService }
        ]
      },
      {
        path: REVERSAL_MATERIAL_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'ReversalMaterialPlan'] as AccessScopesUnion[],
          title: 'MAIN_MENU.REVERSAL_MATERIAL'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadChildren: () =>
          import('./reversal-material-plan/routes').then(({ REVERSAL_MATERIAL_ROUTES }) => REVERSAL_MATERIAL_ROUTES)
      },
      {
        path: MATERIAL_VIEW_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'ReversalMaterialPlan'] as AccessScopesUnion[],
          title: 'APP.TITLE'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideVehicleChecklists(), provideVehiclePosition(), provideVehicleErrorsState(), provideRto()],
        loadChildren: () => import('./material-view/routes').then(({ MATERIAL_VIEW_ROUTES }) => MATERIAL_VIEW_ROUTES)
      },
      {
        path: MATERIAL_REPORT_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'MaterialWrite'] as AccessScopesUnion[],
          title: 'APP.TITLE'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [providePunctuality(), provideMaterialOverview(), provideIduCode()],
        loadChildren: () =>
          import('./material-report/routes').then(({ MATERIAL_REPORT_ROUTES }) => MATERIAL_REPORT_ROUTES)
      },
      {
        path: INCIDENT_MESSAGES_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.INCIDENT_MESSAGES'
        },
        providers: [provideDeviations()],
        loadComponent: () =>
          import('./incident-messages/incident-messages.component').then(
            ({ IncidentMessagesComponent }) => IncidentMessagesComponent
          )
      },
      {
        path: CUSTOMER_ADVISORS_ROUTE,
        canActivate: [SetTitleGuard, HasScopeGuard],
        data: {
          someScopeRequired: ['Admin', 'CustomerAdvisorRead'] as AccessScopesUnion[],
          title: 'MAIN_MENU.CUSTOMER_ADVISOR'
        },
        providers: [provideStations(), provideCustomerAdvisors()],
        loadComponent: () =>
          import('@gan/staff/feature-customer-advisor').then(
            ({ CustomerAdvisorListComponent }) => CustomerAdvisorListComponent
          )
      },
      {
        path: VACATION_APPLICATION_ROUTE,
        canActivate: [SetTitleGuard, HasScopeGuard],
        data: {
          someScopesRequired: ['Admin', 'VacationApplicationWrite'] as AccessScopesUnion[],
          title: 'MAIN_MENU.VACATION_APPLICATION'
        },
        providers: [provideStaffManagement()],
        loadComponent: () =>
          import('@gan/staff/feature-vacation-application').then(
            ({ VacationApplicationComponent }) => VacationApplicationComponent
          )
      },
      {
        path: CLEANING_INFO_ROUTE,
        providers: [provideCleaningInfo()],
        loadChildren: () =>
          import('@gan/common/feature-cleaning-info').then(({ CLEANING_INFO_ROUTES }) => CLEANING_INFO_ROUTES)
      },
      {
        path: STATIONS_MONITOR_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.STATIONS_MONITOR'
        },
        loadChildren: () =>
          import('@gan/common/feature-stations-monitor').then(({ STATIONS_MONITOR_ROUTES }) => STATIONS_MONITOR_ROUTES)
      },
      {
        path: CONTACT_INFO_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.CONTACT_INFO'
        },
        providers: [provideStaffManagement()],
        loadComponent: () =>
          import('@gan/staff/feature-contact-info-search').then(
            ({ ContactInfoSearchComponent }) => ContactInfoSearchComponent
          )
      },
      {
        path: DUTY_KPI_INFO_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.DUTY_KPI'
        },
        loadChildren: () =>
          import('@gan/common/feature-duty-punctuality-kpi-info').then(
            ({ DUTY_KPI_INFO_ROUTES }) => DUTY_KPI_INFO_ROUTES
          ),
        providers: [providePunctuality(), provideStations()]
      },
      {
        path: AI_CHATBOT_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.AI_CHATBOT'
        },
        loadComponent: () =>
          import('@gan/staff/feature-ai-chatbot').then(
            ({ StaffFeatureAiChatbotComponent }) => StaffFeatureAiChatbotComponent
          )
      },
      {
        path: RTV_ROUTE,
        data: {
          someScopeRequired: ['Admin', 'RTV'] as AccessScopesUnion[],
          title: 'MAIN_MENU.RTV'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideBoarding()],
        loadComponent: () =>
          import('@gan/common/feature-train-real-time-timeline').then(
            ({ TrainRealTimeTimelineComponent }) => TrainRealTimeTimelineComponent
          )
      },
      {
        path: HOW_TO,
        canActivate: [SetTitleGuard],
        data: {
          title: 'APP.TITLE'
        },
        loadComponent: () => import('@gan/common/feature-how-to').then(({ HowToComponent }) => HowToComponent),
        providers: [{ provide: SanityNorgeService, useClass: SanityIntraStaffService }]
      },
      {
        path: SETTINGS_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'SETTINGS.TITLE'
        },
        loadComponent: () => import('./settings/settings.component').then(({ SettingsComponent }) => SettingsComponent)
      },
      {
        path: PARKING_PLAN_ROUTE,
        canActivate: [SetTitleGuard],
        data: {
          title: 'MAIN_MENU.PARKING_PLAN'
        },
        providers: [provideParkingPlan()],
        loadComponent: () =>
          import('./parking-plan/parking-plan.component').then(({ ParkingPlanComponent }) => ParkingPlanComponent)
      },
      {
        path: DEPOT_MANAGEMENT_ROUTE,
        loadChildren: () =>
          import('./app-routes-depo').then(({ DEPO_VIEW_ROUTES }) => DEPO_VIEW_ROUTES)
      }
    ]
  },
  {
    path: '**',
    redirectTo: DEFAULT_ROUTE
  }
];

